import * as React from 'react';
import { Product } from '@packages/common';

import { ImpressionTrackerContext } from './ImpressionTrackerContext';
import { RefinementsContext } from '../RefinementsContext';

const TRACKING_VISIBILITY_THRESHOLD = 0.5;

interface ImpressionTrackerProps {
  product: Product;
  index: number;
}

function calculateHorizontalVisiblePixelsInContainer(trackerElement: HTMLDivElement, container: Element): number {
  const { left: trackerLeft, right: trackerRight, width: trackerWidth } = trackerElement.getBoundingClientRect();
  if (!container) {
    return trackerWidth;
  }

  const { left: containerLeft, right: containerRight } = container.getBoundingClientRect();
  const leftCut = Math.min(trackerLeft - containerLeft, 0);
  const rightCut = Math.min(containerRight - trackerRight, 0);
  const horizontalVisiblePixels = trackerWidth + leftCut + rightCut;

  return Math.max(horizontalVisiblePixels, 0);
}

export const ImpressionTracker: React.FC<ImpressionTrackerProps> = ({ product, index, children }) => {
  const { subscribe, condition, getContainer } = React.useContext(ImpressionTrackerContext);
  const { refinements } = React.useContext(RefinementsContext);
  const [searchStateVersion, setSearchState] = React.useState(0);
  const trackerElementRef = React.createRef<HTMLDivElement>();
  const impressionVersion = React.useRef(-1);

  React.useEffect(() => {
    setSearchState((searchState) => searchState + 1);
  }, [refinements]);

  React.useEffect(() => {
    let viewPortSubscription: any | null = null;
    if (subscribe) {
      viewPortSubscription = subscribe(() => {
        if (!trackerElementRef.current) {
          return null;
        }

        // Bail if impression versions are same
        const isVersionDifferent = searchStateVersion !== impressionVersion.current;
        if (!isVersionDifferent) {
          return null;
        }

        const viewBottom = window.innerHeight;
        const { top, bottom, height, width } = trackerElementRef.current.getBoundingClientRect();

        if (bottom < 0 || top > viewBottom) {
          return null;
        }

        const topCut = Math.min(top, 0);
        const bottomCut = Math.min(viewBottom - bottom, 0);
        const verticalVisiblePixels = height + topCut + bottomCut;

        // if there is a container specified for now this only means we check
        // horizontal visibility. We don't have a use case yet for containers
        // that contain impression-trackers that cut them vertically
        const container = getContainer?.();
        const horizontalVisiblePixels = container
          ? calculateHorizontalVisiblePixelsInContainer(trackerElementRef.current, container)
          : width;

        const visiblePixels = horizontalVisiblePixels * verticalVisiblePixels;
        const visiblePixelsRatio = visiblePixels / (height * width);

        if (visiblePixelsRatio <= TRACKING_VISIBILITY_THRESHOLD) {
          return null;
        }

        // Check custom condition meets
        if (condition({ product, index })) {
          // this is used by BI for checking what was tracked.
          // So they can opt-in via the dev-console
          if (window['logImpressions']) {
            console.log(
              '==== NEW IMPRESSION ====\n',
              product.title,
              '\n\nprice:',
              product.price?.value,
              '\nproductId:',
              product.productId,
            );
          }

          impressionVersion.current = searchStateVersion;
          return { item: product, index };
        }

        return null;
      });
    }
    return () => {
      viewPortSubscription && viewPortSubscription.unsubscribe();
    };
  }, [condition, trackerElementRef, index, product, searchStateVersion, subscribe, getContainer]);

  return <div ref={trackerElementRef}>{children}</div>;
};
